import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dumbbell, Play, Check, Trophy, Plus, Trash2, Info, Settings2,
  SlidersHorizontal, Volume2, VolumeX, MoreVertical, Zap, Monitor,
  Users, Edit, SquarePen, Moon, TrendingUp, TrendingDown, Timer, Target,
  SkipForward,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWorkoutMeta } from '../../contexts/WorkoutMetaContext';
import { useWorkoutDataActions } from '../../contexts/WorkoutDataContext';
import { useWorkoutProgress } from '../../contexts/WorkoutProgressContext';
import { DEFAULT_WORKOUT_PREFS } from '../../contexts/WorkoutSessionProvider';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Switch } from '../ui/switch';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import InfoButton from '../ui/info-button';

import RoutineCreateChooser from '../routineWizard/RoutineCreateChooser';
import RoutineWizard from '../routineWizard/RoutineWizard';
import WeekPlannerModal from '../WeekPlannerModal';
import { RoutineEditor, MUSCLE_GROUP_COLORS_MAP } from '../../pages/MyClientsPage';
import { MuscleBodyMapCompact, MuscleDetailModal } from '../MuscleBodyMap';
import TargetMuscleBadges from '../TargetMuscleBadges';
import { getTargetMuscleStats, TARGET_MUSCLE_COLORS } from '../../lib/muscleUtils';
import StreakHero from './StreakHero';
import { ExerciseThumb } from './LiveSession';

import client from '../../api/client';
import { exerciseName, formatDuration, defaultSetCount, getExerciseInputMode } from '../../lib/exerciseDisplay';
import { DIFFICULTY_LEVELS } from '../../lib/constants';
import { useRoutableDialog } from '../../hooks/useRoutableDialog';
import { TA } from '../tour/tourAnchors';
import { primeNotificationPermission } from '../../services/workoutNotificationService';
import {
  getMatchingPreviousSet,
  ensureWorkoutExerciseIds,
  getTodayWorkout,
  getTodayWorkouts,
  getSkippableRoutine,
  getSetDisplayLabel,
} from './liveSessionUtils';

/* ════════════════════════════════════════════════════════════════
   <RoutinePicker />

   The view that renders when there is NO active workout. Owns:
   • Today's workout hero card (or rest-day card)
   • Streak hero
   • Coach-assigned routine grid
   • Personal routines list (with create / edit / delete)
   • Day preview dialog
   • Routine editor dialog (deep-linked via ?routineEditor=)
   • Template selector dialog
   • Workout preferences sheet (?preferences=true)
   • The "update routine after workout?" prompt that fires after a
     personal-routine workout completes
   • The startWorkout / startFromTemplate bridges that flip
     activeWorkout via WorkoutMetaContext

   Reads: meta, data-actions, progress contexts.
   ════════════════════════════════════════════════════════════════ */
export default function RoutinePicker() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const urlOnBehalfOf = searchParams.get('on_behalf_of');

  const meta = useWorkoutMeta();
  const {
    activeWorkout, setActiveWorkout,
    sourcePersonalRoutineId, setSourcePersonalRoutineId,
    sourcePersonalDayId, setSourcePersonalDayId,
    onBehalfOf,
    clientName,
    setWorkoutSessionKey,
    setStartedAt,
    workoutPrefs,
    updatePrefs,
  } = meta;

  const { setWorkoutData, setLastWeights, setAppliedProgression } = useWorkoutDataActions();
  const {
    setCompletedSets,
    sessionPrMaxRef,
    sessionPrRepsRef,
    setIsTimerRunning,
    completedWorkout,
    showUpdateRoutinePrompt, setShowUpdateRoutinePrompt,
  } = useWorkoutProgress();

  const restoredRef = useRef(false);

  // ── Picker-local UI state ──────────────────────────────────
  const [showPreferences, setShowPreferences] = useState(searchParams.get('preferences') === 'true');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [wkMuscleDetailOpen, setWkMuscleDetailOpen] = useState(false);
  const [wkMuscleDetailStats, setWkMuscleDetailStats] = useState({});
  const [wkMuscleDetailName, setWkMuscleDetailName] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const [showPersonalEditor, setShowPersonalEditor] = useState(false);
  const [editingPersonalRoutine, setEditingPersonalRoutine] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [weekPlannerRoutine, setWeekPlannerRoutine] = useState(null);
  const [personalFormName, setPersonalFormName] = useState('');
  const [personalFormDesc, setPersonalFormDesc] = useState('');
  const [deletePersonalConfirm, setDeletePersonalConfirm] = useState(null);
  const [skipConfirm, setSkipConfirm] = useState(null);
  const MAX_CLIENT_ROUTINES = 2;
  const [routineEditorOpen, setRoutineEditorOpen] = useState(null);
  const [routineEditorInitialDay, setRoutineEditorInitialDay] = useState(null);
  const routineEditorDialog = useRoutableDialog('routineEditor');
  const routineEditorCacheRef = useRef(null);
  // URL → state sync (close direction)
  useEffect(() => {
    if (!routineEditorDialog.isOpen && routineEditorOpen !== null) {
      setRoutineEditorOpen(null);
      setRoutineEditorInitialDay(null);
    }
  }, [routineEditorDialog.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (routineEditorOpen) routineEditorCacheRef.current = routineEditorOpen;
  }, [routineEditorOpen]);
  const [dayPreviewOpen, setDayPreviewOpen] = useState(null);

  // Local picker state used when starting workouts (auto-progression deltas etc.).
  const [allExercisesData, setAllExercisesData] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [defaultRestSeconds, setDefaultRestSeconds] = useState({});
  const [progressionSteps] = useState(() => {
    try { return JSON.parse(localStorage.getItem('trainedbyrs_progression_steps') || '{}'); } catch { return {}; }
  });

  /* ── React Query: server data ──────────────────────────────── */
  const assignedRoutineTargetId = urlOnBehalfOf || null;
  const { data: assignedRoutine = null, isLoading: assignedRoutineLoading } = useQuery({
    queryKey: ['assignedRoutine', assignedRoutineTargetId],
    queryFn: async () => {
      const url = assignedRoutineTargetId ? `/users/${assignedRoutineTargetId}/routine` : `/users/me/routine`;
      const response = await client.get(url);
      return response.data || null;
    },
    staleTime: 10 * 60 * 1000,
  });

  const { data: personalRoutines = [], isLoading: personalRoutinesLoading } = useQuery({
    queryKey: ['personalRoutines'],
    queryFn: async () => {
      const response = await client.get('/routines/personal');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });

  // URL → state sync (open direction): when ?routineEditor=<id> appears
  useEffect(() => {
    if (!routineEditorDialog.isOpen) return;
    if (routineEditorOpen?.id === routineEditorDialog.id) return;
    if (routineEditorCacheRef.current?.id === routineEditorDialog.id) {
      setRoutineEditorOpen(routineEditorCacheRef.current);
      return;
    }
    const match = personalRoutines.find(r => r.id === routineEditorDialog.id);
    if (match) {
      routineEditorCacheRef.current = match;
      setRoutineEditorOpen(match);
    }
  }, [routineEditorDialog.isOpen, routineEditorDialog.id, personalRoutines]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: workoutStreak = null } = useQuery({
    queryKey: ['workoutStreak'],
    queryFn: async () => {
      const response = await client.get('/workout-sessions/stats/streak');
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const { data: dailyStreak = null } = useQuery({
    queryKey: ['dailyStreak'],
    queryFn: async () => {
      const response = await client.get('/stats/daily-streak');
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const { data: exerciseImages = {} } = useQuery({
    queryKey: ['exerciseImageMap'],
    queryFn: async () => {
      const res = await client.get('/exercises/image-map');
      return res.data;
    },
    staleTime: 30 * 60 * 1000,
  });

  // Live muscle map from the ExerciseDB — source of truth for day preview stats.
  const { data: exerciseMuscleMap = {} } = useQuery({
    queryKey: ['exerciseMuscleMap'],
    queryFn: async () => {
      const res = await client.get('/exercises/muscle-map');
      return res.data;
    },
    staleTime: 30 * 60 * 1000,
  });

  const { data: allRoutines = [] } = useQuery({
    queryKey: ['allRoutines'],
    queryFn: async () => {
      const response = await client.get('/routines');
      return response.data;
    },
    enabled: showTemplateSelector,
  });

  const loading = assignedRoutineLoading || personalRoutinesLoading;

  const updatePref = (key, value) => {
    const updated = { ...workoutPrefs, [key]: value };
    updatePrefs({ [key]: value });
    clearTimeout(window._prefSaveTimeout);
    window._prefSaveTimeout = setTimeout(() => {
      client.put('/workout-preferences', updated).catch(() => {});
    }, 500);
  };

  useQuery({
    queryKey: ['workoutPreferences'],
    queryFn: async () => {
      const res = await client.get('/workout-preferences');
      if (res.data && Object.keys(res.data).length > 0) {
        updatePrefs(res.data);
      }
      return res.data;
    },
    staleTime: Infinity,
  });

  /* ── Restore from localStorage on mount ─────────────────────
     The provider hydrates activeWorkout / workoutData etc. We only
     restore selectedDay from the active workout (a UI-only mirror). */
  useEffect(() => {
    if (restoredRef.current) return;
    if (activeWorkout) {
      restoredRef.current = true;
      setSelectedDay(activeWorkout);
    }
  }, [activeWorkout]);

  // Auto-start workout from social post
  useEffect(() => {
    const socialDay = location.state?.startFromSocial;
    if (!socialDay) return;
    window.history.replaceState({}, '');
    const timer = setTimeout(() => {
      startWorkout(socialDay);
    }, 300);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreatePersonalRoutine = () => {
    setEditingPersonalRoutine(null);
    setPersonalFormName('');
    setPersonalFormDesc('');
    setShowPersonalEditor(true);
  };

  const openChooserForPersonal = () => { setChooserOpen(true); };
  const handlePickScratch = () => { openCreatePersonalRoutine(); };
  const handlePickWizard = () => { setWizardOpen(true); };

  const handleWizardSave = (aiResult, _mode, opts = {}) => {
    const genId = () => Math.random().toString(36).substr(2, 9);
    const payload = {
      name: aiResult.name || 'AI Generated Routine',
      description: aiResult.description || '',
      days: (aiResult.days || []).map(day => ({
        id: day.id || genId(),
        day_name: day.day_name || 'Day',
        exercises: (day.exercises || []).map(ex => ({
          id: ex.id || genId(),
          name: ex.name || '',
          muscle_group: ex.muscle_group || 'Chest',
          notes: ex.notes || '',
          rest_seconds: ex.rest_seconds || 90,
          sets: Array.isArray(ex.sets) && ex.sets.length > 0
            ? ex.sets.map((s, si) => ({
                set_number: si + 1,
                set_type: s.set_type || 'work',
                target_reps_min: s.target_reps_min || 8,
                target_reps_max: s.target_reps_max || 12,
                weight: s.weight || null,
                completed_reps: null,
                is_pr: false, pr_types: [],
              }))
            : Array.from({ length: typeof ex.sets === 'number' ? ex.sets : defaultSetCount(ex) }).map((_, si) => ({
                set_number: si + 1,
                set_type: 'work',
                target_reps_min: 8,
                target_reps_max: 12,
                weight: null,
                completed_reps: null,
                is_pr: false, pr_types: [],
              })),
        })),
      })),
    };

    if (opts.editFirst) {
      setEditingPersonalRoutine({ ...aiResult, ...payload });
      setPersonalFormName(payload.name);
      setPersonalFormDesc(payload.description);
      setWizardOpen(false);
      setShowPersonalEditor(true);
      return;
    }

    savePersonalRoutineMutation.mutate(
      { payload, isEditing: false, routineId: null },
      {
        onSuccess: (saved) => {
          if (saved?.id && saved?.days) {
            setWeekPlannerRoutine({ ...saved, is_personal: true });
          }
        },
      }
    );
    setWizardOpen(false);
  };

  const savePersonalRoutineMutation = useMutation({
    mutationFn: async ({ payload, isEditing, routineId }) => {
      if (isEditing) {
        const res = await client.put(`/routines/personal/${routineId}`, payload);
        return res.data;
      }
      const res = await client.post('/routines/personal', payload);
      return res.data;
    },
    onSuccess: (_, { isEditing }) => {
      toast.success(isEditing ? t('workout.routineUpdated') : t('workout.routineCreated'));
      setShowPersonalEditor(false);
      queryClient.invalidateQueries({ queryKey: ['personalRoutines'] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || t('workout.failedSaveRoutineGeneric'));
    },
  });

  const savePersonalRoutine = () => {
    if (!personalFormName.trim()) { toast.error(t('workout.routineNameRequired')); return; }
    const payload = {
      name: personalFormName,
      description: personalFormDesc,
      days: editingPersonalRoutine?.days || [],
    };
    savePersonalRoutineMutation.mutate({
      payload,
      isEditing: !!editingPersonalRoutine,
      routineId: editingPersonalRoutine?.id,
    });
  };

  /* Skip ahead one workout in a cycle/rotating schedule.
     Fixes the "permanently one workout behind" trap: the pointer only moves
     when a session is saved, so training without logging leaves it stale. */
  const skipWorkoutMutation = useMutation({
    mutationFn: async ({ routine, isCoach }) => {
      const url = isCoach
        ? `/routines/${routine.id}/skip-workout${urlOnBehalfOf ? `?on_behalf_of=${encodeURIComponent(urlOnBehalfOf)}` : ''}`
        : `/routines/personal/${routine.id}/skip-workout`;
      const res = await client.post(url);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.next_day_name
        ? t('workout.skipWorkoutDone', { day: data.next_day_name })
        : t('workout.skipWorkoutDoneGeneric'));
      setSkipConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['assignedRoutine'] });
      queryClient.invalidateQueries({ queryKey: ['personalRoutines'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || t('workout.skipWorkoutFailed'));
      setSkipConfirm(null);
    },
  });

  const deletePersonalRoutineMutation = useMutation({
    mutationFn: async (id) => {
      await client.delete(`/routines/personal/${id}`);
    },
    onSuccess: () => {
      toast.success(t('workout.routineDeleted'));
      setDeletePersonalConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['personalRoutines'] });
    },
    onError: () => {
      toast.error(t('workout.failedDeleteRoutine'));
      setDeletePersonalConfirm(null);
    },
  });

  const deletePersonalRoutine = (id) => {
    deletePersonalRoutineMutation.mutate(id);
  };

  const saveRoutineFromEditorMutation = useMutation({
    mutationFn: async ({ routine, editedRoutine, sessionId }) => {
      await client.put(`/routines/personal/${routine.id}${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`, {
        name: editedRoutine.name || routine.name,
        description: editedRoutine.description || routine.description || '',
        days: editedRoutine.days,
      });
    },
    onSuccess: () => {
      toast.success(t('workout.routineSaved'));
      queryClient.invalidateQueries({ queryKey: ['personalRoutines'] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || t('workout.failedSaveRoutineGeneric'));
    },
  });

  const saveRoutineFromEditor = (editedRoutine, sessionId) => {
    const routine = routineEditorOpen;
    return saveRoutineFromEditorMutation.mutateAsync({ routine, editedRoutine, sessionId });
  };

  const updatePersonalRoutineDayMutation = useMutation({
    mutationFn: async () => {
      const routine = personalRoutines.find(r => r.id === sourcePersonalRoutineId);
      if (!routine) return;
      const updatedDays = routine.days.map(d => {
        if (d.id !== sourcePersonalDayId) return d;
        return {
          ...d,
          exercises: (completedWorkout.exercises_raw || []).map(ex => ({
            id: ex.exercise_id || Math.random().toString(36).substr(2, 8),
            name: ex.name,
            muscle_group: ex.muscle_group,
            targetMuscles: ex.targetMuscles || [],
            notes: ex.notes || '',
            rest_seconds: ex.rest_seconds || 90,
            sets: (ex.sets || []).map((s, i) => ({
              set_number: i + 1,
              set_type: s.set_type || 'work',
              target_reps_min: s.completed_reps || s.target_reps_min || 8,
              target_reps_max: s.completed_reps || s.target_reps_max || 12,
            }))
          }))
        };
      });
      await client.put(`/routines/personal/${sourcePersonalRoutineId}`, { name: routine.name, description: routine.description || '', days: updatedDays });
    },
    onSuccess: () => {
      toast.success(t('workout.routineUpdatedChanges'));
      setShowUpdateRoutinePrompt(false);
      queryClient.invalidateQueries({ queryKey: ['personalRoutines'] });
    },
    onError: () => {
      toast.error(t('workout.failedUpdateRoutine'));
      setShowUpdateRoutinePrompt(false);
    },
  });

  const updatePersonalRoutineDay = () => {
    if (!sourcePersonalRoutineId || !sourcePersonalDayId || !completedWorkout) return;
    updatePersonalRoutineDayMutation.mutate();
  };

  const enrichExercisesFromDB = async (exerciseNames) => {
    try {
      const res = await client.post('/exercises/enrich-batch', exerciseNames);
      const dataMap = { ...allExercisesData };
      Object.entries(res.data).forEach(([name, data]) => {
        dataMap[name] = data;
      });
      setAllExercisesData(dataMap);
      return res.data;
    } catch {
      return {};
    }
  };

  const startWorkout = async (day) => {
    await primeNotificationPermission();
    const now = Date.now();
    const sessionKey = `${day.id}-${now}`;
    setWorkoutSessionKey(sessionKey);
    setAppliedProgression(new Map());
    sessionPrMaxRef.current = {};
    sessionPrRepsRef.current = {};
    setSelectedDay(day);
    setActiveWorkout(day);
    setStartedAt(now);
    setIsTimerRunning(true);
    setCompletedSets({});
    setSourcePersonalRoutineId(null);
    setSourcePersonalDayId(null);

    const exerciseNames = day.exercises.map(ex => ex.name);
    const [enrichedData, batchWeights] = await Promise.all([
      enrichExercisesFromDB(exerciseNames),
      client.post('/workout-sessions/last-weights-batch', exerciseNames).then(r => r.data).catch(() => ({})),
    ]);

    const restMap = {};
    const lastWeightsMap = { ...batchWeights };
    const initialData = day.exercises.map((exercise, idx) => {
      restMap[idx] = exercise.rest_seconds || 90;
      const enriched = (enrichedData || {})[exercise.name] || {};
      return {
        exercise_id: exercise.id, name: exercise.name, muscle_group: exercise.muscle_group,
        notes: exercise.notes, is_adhoc: false, rest_seconds: exercise.rest_seconds || 90,
        image_url: enriched.image_url,
        image_url_2: enriched.image_url_2,
        gifUrl: enriched.gifUrl || enriched.image_url,
        instructions: enriched.instructions,
        instructions_list: enriched.instructions_list,
        primary_muscles: enriched.primary_muscles || enriched.targetMuscles,
        secondary_muscles: enriched.secondary_muscles || enriched.secondaryMuscles,
        targetMuscles: enriched.targetMuscles,
        secondaryMuscles: enriched.secondaryMuscles,
        equipment: enriched.equipment,
        sets: exercise.sets.map((set, index) => ({
          set_number: set.set_number, set_type: set.set_type || 'work',
          target_reps_min: set.target_reps_min, target_reps_max: set.target_reps_max,
          from_routine: true,
          weight: getMatchingPreviousSet(lastWeightsMap[exercise.name]?.weights, exercise.sets, index)?.weight || 0,
          completed_reps: 0, is_pr: false, pr_types: []
        }))
      };
    });
    setLastWeights(prev => ({ ...prev, ...lastWeightsMap }));
    setDefaultRestSeconds(restMap);

    let finalData;
    if (workoutPrefs.fullAutoProgression) {
      const autoApplied = new Map();
      const autoData = initialData.map((ex, exIdx) => {
        const lastWeight = lastWeightsMap[ex.name];
        if (!lastWeight) return ex;
        const sugIncrease = lastWeight.suggested_increase || 0;
        const sugDecrease = lastWeight.suggested_decrease || 0;
        if (!sugIncrease && !sugDecrease) return ex;
        const direction = sugIncrease > 0 ? 'increase' : 'decrease';
        const step = progressionSteps[ex.name] || (direction === 'increase' ? workoutPrefs.autoProgressStep : workoutPrefs.autoDeloadStep);
        const delta = direction === 'increase' ? step : -step;
        autoApplied.set(`${sessionKey}-${exIdx}`, direction);
        return {
          ...ex,
          sets: ex.sets.map(s =>
            (s.set_type === 'work' || !s.set_type)
              ? { ...s, weight: Math.max(0, Math.round(((s.weight || 0) + delta) * 10) / 10) }
              : s
          )
        };
      });
      finalData = ensureWorkoutExerciseIds(autoData);
      setWorkoutData(finalData);
      setAppliedProgression(autoApplied);
      if (autoApplied.size > 0) {
        toast.success(t('workout.autoProgressApplied', { count: autoApplied.size }));
      }
    } else {
      finalData = ensureWorkoutExerciseIds(initialData);
      setWorkoutData(finalData);
    }
    navigate(`/workout/${sessionKey}`);
  };

  const startEmptyWorkout = () => {
    if (user?.role === 'client') {
      startTrulyEmptyWorkout();
      return;
    }
    setShowTemplateSelector(true);
  };

  const startFromTemplate = async (routine, day) => {
    await primeNotificationPermission();
    setShowTemplateSelector(false);
    let fullDay = day;
    if (!day.exercises?.[0]?.sets?.length) {
      try {
        const fullRoutine = await client.get(`/routines/${routine.id}`);
        const matchedDay = fullRoutine.data.days?.find(d => d.id === day.id);
        if (matchedDay) fullDay = matchedDay;
      } catch (err) {
        console.error('Failed to fetch full routine:', err);
      }
    }
    const now = Date.now();
    const workoutDay = {
      id: 'template-' + Date.now(),
      day_name: `${routine.name} - ${fullDay.day_name}`,
      exercises: fullDay.exercises.map(ex => ({ ...ex, id: ex.id + '-' + Date.now() }))
    };
    if (routine.is_personal || routine.owner_id) {
      setSourcePersonalRoutineId(routine.id);
      setSourcePersonalDayId(day.id);
    } else {
      setSourcePersonalRoutineId(null);
      setSourcePersonalDayId(null);
    }
    const sessionKey = `${workoutDay.id}-${now}`;
    setWorkoutSessionKey(sessionKey);
    setAppliedProgression(new Map());
    setSelectedDay(workoutDay);
    setActiveWorkout(workoutDay);
    setStartedAt(now);
    setIsTimerRunning(true);
    setCompletedSets({});

    const exerciseNames = workoutDay.exercises.map(ex => ex.name);
    const [enrichedData, batchWeights] = await Promise.all([
      enrichExercisesFromDB(exerciseNames),
      client.post('/workout-sessions/last-weights-batch', exerciseNames).then(r => r.data).catch(() => ({})),
    ]);

    const restMap = {};
    const lastWeightsMap = { ...batchWeights };
    const initialData = workoutDay.exercises.map((exercise, idx) => {
      restMap[idx] = exercise.rest_seconds || 90;
      const enriched = enrichedData[exercise.name] || {};
      return {
        exercise_id: exercise.id, name: exercise.name, muscle_group: exercise.muscle_group,
        notes: exercise.notes || '', is_adhoc: false, rest_seconds: exercise.rest_seconds || 90,
        image_url: enriched.image_url,
        image_url_2: enriched.image_url_2,
        gifUrl: enriched.gifUrl || enriched.image_url,
        instructions: enriched.instructions,
        instructions_list: enriched.instructions_list || [],
        targetMuscles: enriched.targetMuscles || enriched.primary_muscles || [],
        secondaryMuscles: enriched.secondaryMuscles || enriched.secondary_muscles || [],
        primary_muscles: enriched.primary_muscles,
        secondary_muscles: enriched.secondary_muscles,
        equipment: enriched.equipment,
        sets: exercise.sets.map((set, idx) => ({
          set_number: set.set_number, set_type: set.set_type || 'work',
          target_reps_min: set.target_reps_min, target_reps_max: set.target_reps_max,
          from_routine: true,
          weight: getMatchingPreviousSet(lastWeightsMap[exercise.name]?.weights, exercise.sets, idx)?.weight || 0,
          completed_reps: 0, is_pr: false, pr_types: []
        }))
      };
    });
    setLastWeights(prev => ({ ...prev, ...lastWeightsMap }));
    setDefaultRestSeconds(restMap);

    let finalDataTemplate;
    if (workoutPrefs.fullAutoProgression) {
      const autoApplied = new Map();
      const autoData = initialData.map((ex, exIdx) => {
        const lastWeight = lastWeightsMap[ex.name];
        if (!lastWeight) return ex;
        const sugIncrease = lastWeight.suggested_increase || 0;
        const sugDecrease = lastWeight.suggested_decrease || 0;
        if (!sugIncrease && !sugDecrease) return ex;
        const direction = sugIncrease > 0 ? 'increase' : 'decrease';
        const step = progressionSteps[ex.name] || (direction === 'increase' ? workoutPrefs.autoProgressStep : workoutPrefs.autoDeloadStep);
        const delta = direction === 'increase' ? step : -step;
        autoApplied.set(`${sessionKey}-${exIdx}`, direction);
        return {
          ...ex,
          sets: ex.sets.map(s =>
            (s.set_type === 'work' || !s.set_type)
              ? { ...s, weight: Math.max(0, Math.round(((s.weight || 0) + delta) * 10) / 10) }
              : s
          )
        };
      });
      finalDataTemplate = ensureWorkoutExerciseIds(autoData);
      setWorkoutData(finalDataTemplate);
      setAppliedProgression(autoApplied);
      if (autoApplied.size > 0) {
        toast.success(t('workout.autoProgressApplied', { count: autoApplied.size }));
      }
    } else {
      finalDataTemplate = ensureWorkoutExerciseIds(initialData);
      setWorkoutData(finalDataTemplate);
    }
    navigate(`/workout/${sessionKey}`);
  };

  const startTrulyEmptyWorkout = () => {
    setShowTemplateSelector(false);
    const now = Date.now();
    const emptyDay = { id: 'empty-' + Date.now(), day_name: 'Custom Workout', exercises: [] };
    const emptySessionKey = `${emptyDay.id}-${now}`;
    setSelectedDay(emptyDay);
    setActiveWorkout(emptyDay);
    setStartedAt(now);
    setIsTimerRunning(true);
    setCompletedSets({});
    setWorkoutData([]);
    setSourcePersonalRoutineId(null);
    setSourcePersonalDayId(null);
    navigate(`/workout/${emptySessionKey}`);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;
  }

  /* ════════════════════════════════════════════════════════════
     ROUTINE SELECTION VIEW
     ════════════════════════════════════════════════════════════ */
  const todayWorkouts = getTodayWorkouts(assignedRoutine, personalRoutines);
  const todayWorkout = getTodayWorkout(assignedRoutine, personalRoutines); // for rest/null detection
  // Non-null only for cycle/rotating schedules — weekly has no pointer to advance.
  const skippable = getSkippableRoutine(assignedRoutine, personalRoutines);
  return (
    <div className="space-y-6 pb-20 md:pb-0" data-testid="workout-page">
      {onBehalfOf && clientName && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-primary/30 bg-primary/10" data-testid="on-behalf-banner">
          <Users className="h-5 w-5 text-primary shrink-0" />
          <p className="text-sm font-medium">{t('workout.loggingOnBehalfOf')} <span className="text-primary font-bold">{decodeURIComponent(clientName)}</span></p>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-tour-anchor={TA.ROUTINES_HEADER}>
            {t('workout.yourWorkout')} <span className="text-primary">{t('workout.workoutLabel')}</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            {assignedRoutine ? t('workout.selectDay') : t('workout.selectDayCustom')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={startEmptyWorkout} className="bg-primary hover:bg-primary/90 flex-1 md:flex-none" data-testid="start-empty-workout-btn">
            <Plus className="mr-2 h-4 w-4" /> {t('workout.startCustomWorkout')}
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setShowPreferences(true)}
                className="w-10 h-10 rounded-xl flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted transition-colors border border-border"
                data-testid="workout-preferences-btn" aria-label={t('tooltips.workoutPreferences')}
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('tooltips.workoutPreferences')}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Today's Workout Hero(s) */}
      {!loading && (() => {
        if (todayWorkouts.length === 0) {
          if (todayWorkout?.isRest) {
            return (
              <div className="flex items-center gap-4 p-4 rounded-2xl border border-border/50 bg-muted/20" data-testid="today-rest-card">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <Moon className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-bold text-base" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('workout.restDayTitle')}</p>
                  <p className="text-sm text-muted-foreground">{t('workout.restDayDesc')}</p>
                </div>
              </div>
            );
          }
          return null;
        }
        return (
          <div className="space-y-4" data-testid="today-workouts">
            {todayWorkouts.map(({ routine: todayRoutine, day: todayDay, isCoach: todayIsCoach }, idx) => {
              const todayExercises = todayDay.exercises || [];
              const todayTotalSets = todayExercises.reduce((acc, ex) => acc + (ex.sets?.length || 0), 0);
              const todayMuscles = [...new Set(todayExercises.flatMap(ex => ex.targetMuscles || []))].slice(0, 5);
              const startThis = todayIsCoach ? () => startWorkout(todayDay) : () => startFromTemplate(todayRoutine, todayDay);
              return (
                <div className="relative" data-testid="today-workout-hero" key={`${todayRoutine.id}-${todayDay.id}`}>
                  <div className="absolute -top-2 left-4 z-10">
                    <Badge className="bg-primary text-white text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 shadow-md">
                      {todayWorkouts.length > 1 ? t('workout.todayBadgeNumbered', { num: idx + 1 }) : t('workout.todayBadge')}
                    </Badge>
                  </div>
                  <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-transparent">
                    <CardContent className="pt-7 pb-5">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="min-w-0">
                          <h2 className="text-3xl font-bold leading-tight truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                            {todayDay.day_name?.toUpperCase()}
                          </h2>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {todayRoutine.name} · <span className={todayIsCoach ? 'text-primary' : 'text-blue-400'}>{todayIsCoach ? t('workout.coachAssigned') : t('workout.personalBadge')}</span>
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{todayExercises.length}</p>
                          <p className="text-xs text-muted-foreground">{t('workout.exercisesCount', { count: todayExercises.length })}</p>
                          <p className="text-xs text-muted-foreground">{todayTotalSets} {t('workout.setsLabel')}</p>
                        </div>
                      </div>
                      {todayMuscles.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {todayMuscles.map(m => (
                            <Badge key={m} variant="secondary" className={`text-xs capitalize ${TARGET_MUSCLE_COLORS[m] || ''}`}>{m}</Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1 h-11"
                          onClick={() => setDayPreviewOpen({ routine: todayRoutine, day: todayDay, isCoach: todayIsCoach, onStart: startThis })}
                          data-testid={`today-preview-btn-${idx}`}
                        >
                          <Info className="mr-2 h-4 w-4" /> {t('workout.previewStartWorkout')}
                        </Button>
                        <Button
                          className="flex-1 h-11 bg-primary hover:bg-primary/90 font-bold"
                          onClick={startThis}
                          data-testid={`today-start-btn-${idx}`}
                        >
                          <Play className="mr-2 h-4 w-4" /> {t('workout.startWorkout')}
                        </Button>
                      </div>
                      {/* Already trained this one off-app? Nudge the pointer so the
                          schedule doesn't stay permanently one workout behind. */}
                      {skippable && idx === todayWorkouts.length - 1 && (
                        <button
                          onClick={() => setSkipConfirm(skippable)}
                          disabled={skipWorkoutMutation.isPending}
                          className="mt-3 w-full min-h-[44px] flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                          data-testid="skip-workout-btn"
                        >
                          <SkipForward className="h-3.5 w-3.5" /> {t('workout.skipWorkout')}
                        </button>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Streak Hero */}
      <StreakHero dailyStreak={dailyStreak} workoutStreak={workoutStreak} />

      {/* Coach-Assigned Routine */}
      {assignedRoutine && (
        <>
          <div className="relative">
            <div className="absolute -top-2 left-4 z-10">
              <Badge className="bg-primary text-white text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 shadow-md" data-testid="coach-assigned-badge">
                {t('workout.coachAssigned')}
              </Badge>
            </div>
            <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
              <CardHeader className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{assignedRoutine.name?.toUpperCase()}</CardTitle>
                    <CardDescription>{assignedRoutine.description || t('workout.assignedRoutineDesc')}</CardDescription>
                  </div>
                  {assignedRoutine.difficulty && (
                    <Badge className={
                      assignedRoutine.difficulty === 'Beginner' ? 'bg-green-500' :
                      assignedRoutine.difficulty === 'Advanced' ? 'bg-red-500' : 'bg-yellow-500'
                    }>{assignedRoutine.difficulty}</Badge>
                  )}
                </div>
              </CardHeader>
            </Card>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assignedRoutine.days?.map((day, index) => (
              <Card key={day.id} className="border-border/50 hover:border-primary/50 transition-colors cursor-pointer group"
                onClick={() => setDayPreviewOpen({ routine: assignedRoutine, day, isCoach: true, onStart: () => startWorkout(day) })}
                data-testid={`workout-day-${index}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-white font-bold text-xl">{index + 1}</div>
                    <Play className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{(day.day_name || '').toUpperCase()}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{day.exercises?.length || 0} {t('workout.statExercises').toLowerCase()} | {day.exercises?.reduce((acc, ex) => acc + (ex.sets?.length || 0), 0) || 0} {t('workout.setsLabel')}</p>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set((day.exercises || []).flatMap(ex => ex.targetMuscles || []))].slice(0, 4).map(m => (
                      <Badge key={m} variant="secondary" className={`text-xs capitalize ${TARGET_MUSCLE_COLORS[m] || ''}`}>{m}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Personal Routines Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('workout.myRoutinesTitle')} <span className="text-primary">{t('workout.myRoutinesLabel')}</span>
            </h2>
            {personalRoutines.length > 0 && <Badge variant="secondary" className="text-xs">{personalRoutines.length}{user?.role === 'client' ? `/${MAX_CLIENT_ROUTINES}` : ''}</Badge>}
          </div>
          <Button
            size="sm"
            className="bg-primary hover:bg-primary/90"
            onClick={openChooserForPersonal}
            data-testid="create-personal-routine-workout-btn"
            data-tour-anchor={TA.ROUTINES_CREATE_BTN}
          >
            <Plus className="mr-1 h-4 w-4" /> {t('workout.createRoutineBtn')}
          </Button>
        </div>

        {personalRoutines.length === 0 && (
          <Card className="border-dashed border-2 border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Dumbbell className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground text-center">{t('workout.noPersonalRoutines')}</p>
            </CardContent>
          </Card>
        )}

        {personalRoutines.map((routine) => (
          <Card key={routine.id} className="border-border/50" data-testid={`personal-routine-${routine.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {/* Was `truncate` which clipped to 4 chars ("BACK...") when
                        a long name competed with the edit/delete icons on the
                        right. Use line-clamp-2 + break-words on small viewports
                        so the routine name stays identifiable. */}
                    <CardTitle className="text-lg line-clamp-2 sm:truncate break-words min-w-0" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{routine.name?.toUpperCase()}</CardTitle>
                    <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/30 text-xs px-1.5 py-0 flex-shrink-0">{t('workout.personalBadge')}</Badge>
                  </div>
                  {routine.description && <CardDescription className="mt-0.5">{routine.description}</CardDescription>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {routine.difficulty && (
                    <Badge className={
                      routine.difficulty === 'Beginner' ? 'bg-green-500' :
                      routine.difficulty === 'Advanced' ? 'bg-red-500' : 'bg-yellow-500'
                    }>{routine.difficulty}</Badge>
                  )}
                  <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8" onClick={() => { setRoutineEditorInitialDay(null); setRoutineEditorOpen(routine); routineEditorDialog.open(routine.id); }} data-testid={`edit-personal-routine-${routine.id}`} tooltip={t('tooltips.edit')}>
                    <Edit className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8" onClick={() => setDeletePersonalConfirm(routine.id)} data-testid={`delete-personal-routine-${routine.id}`} tooltip={t('tooltips.delete')}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {routine.days?.map((day) => (
                  <div key={day.id} className="relative group">
                    <Button variant="outline"
                      className="w-full h-auto py-3 px-3 flex flex-col items-start gap-1 hover:border-primary hover:bg-primary/90"
                      onClick={() => setDayPreviewOpen({ routine, day, isCoach: false, onStart: () => startFromTemplate(routine, day) })}
                      data-testid={`personal-day-${day.id}`}>
                      <span className="font-medium text-sm">{day.day_name}</span>
                      <span className="text-xs text-muted-foreground">{day.exercises?.length || 0} exercises</span>
                    </Button>
                    <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button onClick={(e) => { e.stopPropagation(); startFromTemplate(routine, day); }}
                            className="w-9 h-9 md:w-6 md:h-6 rounded-md bg-primary text-white flex items-center justify-center hover:bg-primary/90 shadow-sm"
                            data-testid={`start-day-${day.id}`}>
                            <Play className="h-3 w-3 ml-0.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{t('tooltips.startWorkout')}</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                ))}
                {/* Add Day Button — opens RoutineEditor */}
                <button
                  onClick={() => { setRoutineEditorInitialDay(null); setRoutineEditorOpen(routine); }}
                  className="h-[62px] rounded-xl border-2 border-dashed border-border/50 hover:border-primary/50 hover:bg-primary/90 flex flex-col items-center justify-center gap-1 transition-colors"
                  data-testid={`add-day-btn-${routine.id}`}
                >
                  <Plus className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{t('workout.addDayBtn')}</span>
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* No Routine Message */}
      {!assignedRoutine && personalRoutines.length === 0 && (
        <Card className="border-border/50 mt-2">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <p className="text-sm text-muted-foreground text-center">{user?.role === 'client' ? t('workout.clientNoRoutine') : t('workout.contactCoach')}</p>
          </CardContent>
        </Card>
      )}

      {/* Update Routine After Workout Prompt */}
      <Dialog open={showUpdateRoutinePrompt} onOpenChange={(open) => { if (!open) { setShowUpdateRoutinePrompt(false); setSelectedDay(null); setSourcePersonalRoutineId(null); setSourcePersonalDayId(null); } }}>
        <DialogContent className="max-w-sm" data-testid="update-routine-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Dumbbell className="h-5 w-5 text-primary" /> {t('workout.updateRoutineTitle')} <span className="text-primary">{t('workout.updateRoutineLabel')}</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('workout.updateRoutineBody')}
          </p>
          <p className="text-xs text-muted-foreground/70">{t('workout.updateRoutineNote')}</p>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => { setShowUpdateRoutinePrompt(false); setSelectedDay(null); setSourcePersonalRoutineId(null); setSourcePersonalDayId(null); }} className="flex-1" data-testid="skip-update-routine-btn">
              {t('workout.noKeepOriginal')}
            </Button>
            <Button onClick={updatePersonalRoutineDay} className="flex-1 bg-primary hover:bg-primary/90" data-testid="confirm-update-routine-btn">
              <Check className="mr-1 h-4 w-4" /> {t('workout.yesUpdate')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Personal Routine Create/Edit Dialog */}
      <Dialog open={showPersonalEditor} onOpenChange={setShowPersonalEditor}>
        <DialogContent className="max-w-sm" data-testid="personal-routine-editor-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {editingPersonalRoutine ? t('workout.editRoutineTitle') : t('workout.createRoutineTitle')} <span className="text-primary">ROUTINE</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.routineNameInputLabel')}</label>
              <Input
                value={personalFormName}
                onChange={e => setPersonalFormName(e.target.value)}
                placeholder={t('workout.routineNameInputPlaceholder')}
                className="mt-1"
                data-testid="personal-routine-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.routineDescLabel')}</label>
              <Input
                value={personalFormDesc}
                onChange={e => setPersonalFormDesc(e.target.value)}
                placeholder={t('workout.routineDescPlaceholder')}
                className="mt-1"
                data-testid="personal-routine-desc-input"
              />
            </div>
            {editingPersonalRoutine && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.workoutDaysLabel')}</label>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('workout.workoutDaysNote', { count: editingPersonalRoutine.days?.length || 0 })}
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPersonalEditor(false)} className="flex-1">{t('common.cancel')}</Button>
            <Button onClick={savePersonalRoutine} className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-personal-routine-btn">
              <Check className="mr-2 h-4 w-4" /> {editingPersonalRoutine ? t('common.update') : t('common.create')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Personal Routine Confirmation */}
      <Dialog open={deletePersonalConfirm !== null} onOpenChange={(open) => { if (!open) setDeletePersonalConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-personal-routine-dialog">
          <DialogTitle className="text-lg font-bold">{t('workout.deleteRoutineTitle')}</DialogTitle>
          <p className="text-sm text-muted-foreground">{t('workout.deleteRoutineBody')}</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeletePersonalConfirm(null)} className="flex-1">{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={() => deletePersonalRoutine(deletePersonalConfirm)} className="flex-1" data-testid="confirm-delete-personal-btn">{t('common.delete')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Skip Workout Confirmation */}
      <Dialog open={skipConfirm !== null} onOpenChange={(open) => { if (!open) setSkipConfirm(null); }}>
        {/* `compact` keeps this a centred card on mobile — without it DialogContent
            expands to a full-screen overlay, which is wrong for a 2-line confirm. */}
        <DialogContent className="max-w-sm" compact data-testid="skip-workout-dialog">
          <DialogTitle className="text-lg font-bold">{t('workout.skipWorkoutTitle')}</DialogTitle>
          <p className="text-sm text-muted-foreground">{t('workout.skipWorkoutBody')}</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setSkipConfirm(null)} className="flex-1">{t('common.cancel')}</Button>
            <Button
              onClick={() => skipWorkoutMutation.mutate(skipConfirm)}
              disabled={skipWorkoutMutation.isPending}
              className="flex-1"
              data-testid="confirm-skip-workout-btn"
            >
              {t('workout.skipWorkoutConfirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Day Preview Dialog */}
      <Dialog open={dayPreviewOpen !== null} onOpenChange={(open) => { if (!open) setDayPreviewOpen(null); }}>
        <DialogContent className="w-full h-[100dvh] max-w-none m-0 rounded-none sm:rounded-2xl sm:max-w-lg sm:max-h-[90dvh] sm:h-auto sm:m-auto p-0 overflow-hidden flex flex-col" data-testid="day-preview-dialog">
          {dayPreviewOpen && (() => {
            const { routine: prevRoutine, day: prevDay } = dayPreviewOpen;
            const exercises = prevDay.exercises || [];
            const totalSets = exercises.reduce((acc, ex) => acc + (Array.isArray(ex.sets) ? ex.sets.length : 0), 0);
            const workSets = exercises.reduce((acc, ex) => acc + (Array.isArray(ex.sets) ? ex.sets.filter(s => (s.set_type || 'work') === 'work' || s.set_type === 'failure').length : 0), 0);
            const { total: muscleStats } = getTargetMuscleStats([prevDay], true, exerciseMuscleMap);
            return (
              <div className="flex flex-col flex-1 min-h-0">
                {/* Header */}
                <div className="px-5 pt-5 pb-3 shrink-0 border-b border-border/20">
                  <div className="flex items-center gap-2 pr-8">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-2xl font-bold leading-tight truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {prevDay.day_name?.toUpperCase()}
                      </h2>
                      <p className="text-xs text-muted-foreground mt-0.5">{prevRoutine.name} · {exercises.length} ex · {totalSets} sets · {workSets} work</p>
                    </div>
                    {!dayPreviewOpen.isCoach && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => {
                              const dayIdx = prevRoutine.days?.findIndex(d => d.id === prevDay.id);
                              setDayPreviewOpen(null);
                              setRoutineEditorInitialDay(dayIdx >= 0 ? dayIdx : null);
                              setRoutineEditorOpen(prevRoutine);
                              routineEditorDialog.open(prevRoutine.id);
                            }}
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/90 transition-colors shrink-0"
                            data-testid="day-preview-edit-btn"
                          >
                            <SquarePen className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{t('tooltips.editRoutine')}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto overscroll-contain">
                  {/* Muscle Stats + Body Map */}
                  {Object.keys(muscleStats).length > 0 && (
                    <div className="px-4 py-3 border-b border-border/20">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <TargetMuscleBadges stats={muscleStats} size="sm" />
                        </div>
                        <div className="shrink-0">
                          <MuscleBodyMapCompact
                            muscleStats={muscleStats}
                            colorMode="daily"
                            onClick={() => {
                              setWkMuscleDetailStats(muscleStats);
                              setWkMuscleDetailName(prevRoutine?.name || 'Workout');
                              setWkMuscleDetailOpen(true);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Exercise List */}
                  <div className="px-4 py-3 space-y-2">
                    {exercises.length === 0 ? (
                      <div className="text-center py-10 text-muted-foreground">
                        <Dumbbell className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">{t('workout.noExercisesPreview')}</p>
                        <p className="text-xs mt-1">{t('workout.openEditorToAdd')}</p>
                      </div>
                    ) : exercises.map((ex, exIdx) => {
                      const sets = ex.sets || [];
                      const isCardioEx = getExerciseInputMode(ex) === 'cardio';
                      const warmupSets = sets.filter(s => s.set_type === 'warmup');
                      const exWorkSets = sets.filter(s => (s.set_type || 'work') === 'work');
                      const failureSets = sets.filter(s => s.set_type === 'failure');
                      const backoffSets = sets.filter(s => s.set_type === 'backoff');
                      const dropSets = sets.filter(s => s.set_type === 'drop');
                      return (
                        <div key={exIdx} className="rounded-2xl border border-border/50 bg-card p-3" data-testid={`preview-exercise-${exIdx}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-neutral-800 flex items-center justify-center shrink-0 overflow-hidden">
                              <ExerciseThumb name={ex.name} gifUrl={ex.gifUrl || ex.image_url} exerciseImages={exerciseImages} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm leading-tight">{exerciseName(ex, language)}</p>
                              <div className="flex flex-wrap items-center gap-1 mt-1">
                                {(ex.targetMuscles || []).length > 0 ? (
                                  (ex.targetMuscles || []).map(m => (
                                    <span key={m} className={`text-xs px-1.5 py-0 rounded-md border font-semibold capitalize ${TARGET_MUSCLE_COLORS[m] || 'bg-muted text-muted-foreground border-border/50'}`}>
                                      {m}
                                    </span>
                                  ))
                                ) : ex.muscle_group ? (
                                  <span className={`text-xs px-1.5 py-0 rounded-md border font-semibold ${MUSCLE_GROUP_COLORS_MAP[ex.muscle_group] || 'bg-muted text-muted-foreground border-border/50'}`}>
                                    {ex.muscle_group}
                                  </span>
                                ) : null}
                                {ex.equipment && <span className="text-xs text-muted-foreground">{ex.equipment}</span>}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-sm font-bold text-primary">{sets.length}</p>
                              <p className="text-xs text-muted-foreground">sets</p>
                            </div>
                          </div>
                          {sets.length > 0 && (
                            <div className="mt-2.5 pt-2.5 border-t border-border/20">
                              <div className="flex flex-wrap gap-1 mb-1.5">
                                {warmupSets.length > 0 && <span className="text-xs px-1.5 py-0.5 rounded-lg bg-yellow-500/15 text-yellow-500 border border-yellow-500/25 font-medium">{warmupSets.length} warmup</span>}
                                {exWorkSets.length > 0 && <span className="text-xs px-1.5 py-0.5 rounded-lg bg-primary/15 text-primary border border-primary/25 font-medium">{exWorkSets.length} work</span>}
                                {failureSets.length > 0 && <span className="text-xs px-1.5 py-0.5 rounded-lg bg-red-600/15 text-red-500 border border-red-600/25 font-medium">{failureSets.length} failure</span>}
                                {backoffSets.length > 0 && <span className="text-xs px-1.5 py-0.5 rounded-lg bg-sky-500/15 text-sky-400 border border-sky-500/25 font-medium">{backoffSets.length} back-off</span>}
                                {dropSets.length > 0 && <span className="text-xs px-1.5 py-0.5 rounded-lg bg-purple-500/15 text-purple-400 border border-purple-500/25 font-medium">{dropSets.length} drop</span>}
                              </div>
                              <div className="grid grid-cols-2 gap-1">
                                {sets.map((s, sIdx) => {
                                  const setLabel = getSetDisplayLabel(sets, sIdx);
                                  const setType = s.set_type || 'work';
                                  const labelColor =
                                    setType === 'warmup' ? 'text-yellow-500'
                                    : setType === 'backoff' ? 'text-sky-400'
                                    : setType === 'failure' ? 'text-red-500'
                                    : setType === 'drop' ? 'text-purple-400'
                                    : 'text-foreground/70';
                                  const repsText = s.duration_seconds > 0
                                    ? formatDuration(s.duration_seconds)
                                    : s.target_duration_seconds
                                    ? `${Math.round(s.target_duration_seconds / 60)} min`
                                    : isCardioEx
                                    ? '—'
                                    : `${
                                        s.reps ||
                                        (s.target_reps_min && s.target_reps_max && s.target_reps_min !== s.target_reps_max
                                          ? `${s.target_reps_min}-${s.target_reps_max}`
                                          : s.target_reps_min || s.target_reps_max || '-')
                                      } reps${s.weight ? ` @ ${s.weight}kg` : ''}`;
                                  return (
                                    <div key={sIdx} className="text-xs text-muted-foreground bg-muted/30 rounded-lg px-2 py-1">
                                      <span className={`font-medium ${labelColor}`}>Set {setLabel}</span> · {repsText}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Start Workout Button */}
                <div className="px-4 pt-3 shrink-0 bg-background border-t border-border/20" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
                  <Button
                    className="w-full h-14 bg-primary hover:bg-primary/90 text-white text-base font-bold rounded-2xl"
                    onClick={() => { setDayPreviewOpen(null); dayPreviewOpen.onStart?.(); }}
                    data-testid="day-preview-start-workout-btn"
                  >
                    <Play className="mr-2 h-5 w-5" /> {t('workout.startWorkout')}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Routine Editor */}
      {routineEditorOpen && (
        <Dialog open={!!routineEditorOpen} onOpenChange={(open) => { if (!open) { setRoutineEditorOpen(null); setRoutineEditorInitialDay(null); routineEditorDialog.close(); queryClient.invalidateQueries({ queryKey: ['personalRoutines'] }); } }}>
          <DialogContent className="w-full h-[100dvh] max-w-none m-0 rounded-none sm:rounded-xl sm:max-w-2xl sm:h-[90dvh] sm:m-auto p-0 overflow-hidden flex flex-col [&>button:last-child]:hidden">
            <RoutineEditor
              routine={routineEditorOpen}
              clientUser={user}
              showNameEditor={true}
              initialDayIdx={routineEditorInitialDay}
              onClose={() => { setRoutineEditorOpen(null); setRoutineEditorInitialDay(null); routineEditorDialog.close(); queryClient.invalidateQueries({ queryKey: ['personalRoutines'] }); }}
              onSave={async (editedRoutine, { sessionId } = {}) => {
                await saveRoutineFromEditor(editedRoutine, sessionId);
                setRoutineEditorOpen(prev => ({ ...prev, ...editedRoutine }));
                queryClient.invalidateQueries({ queryKey: ['personalRoutines'] });
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Template Selector Dialog */}
      <Dialog open={showTemplateSelector} onOpenChange={setShowTemplateSelector}>
        <DialogContent className="max-w-4xl max-h-[85dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              START <span className="text-primary">{t('workout.workoutLabel')}</span>
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{t('workout.templateSelector.subtitle')}</p>
          </DialogHeader>
          <div className="flex items-center gap-2 py-2">
            <Button variant={templateFilter === 'all' ? 'default' : 'outline'} size="sm"
              onClick={() => setTemplateFilter('all')}
              className={templateFilter === 'all' ? 'bg-primary' : ''}>{t('common.all')}</Button>
            {DIFFICULTY_LEVELS.map(level => (
              <Button key={level} variant={templateFilter === level ? 'default' : 'outline'} size="sm"
                onClick={() => setTemplateFilter(level)}
                className={templateFilter === level ? (level === 'Beginner' ? 'bg-green-500' : level === 'Advanced' ? 'bg-red-500' : 'bg-yellow-500') : ''}>
                {level}
              </Button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(85dvh - 200px)' }}>
            <Card className="border-dashed border-2 border-primary/30 hover:border-primary transition-colors cursor-pointer mb-4" onClick={startTrulyEmptyWorkout}>
              <CardContent className="flex items-center gap-4 py-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Plus className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('workout.templateSelector.startFromScratch')}</h3>
                  <p className="text-sm text-muted-foreground">{t('workout.templateSelector.buildOwn')}</p>
                </div>
              </CardContent>
            </Card>
            {allRoutines.filter(r => templateFilter === 'all' || r.difficulty === templateFilter).map(routine => (
              <Card key={routine.id} className="border-border/50 mb-4">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-lg truncate min-w-0" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{routine.name?.toUpperCase()}</CardTitle>
                    <Badge className={`flex-shrink-0 ${routine.difficulty === 'Beginner' ? 'bg-green-500' : routine.difficulty === 'Advanced' ? 'bg-red-500' : 'bg-yellow-500'}`}>
                      {routine.difficulty || 'Intermediate'}
                    </Badge>
                  </div>
                  {routine.description && <CardDescription>{routine.description}</CardDescription>}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {routine.days?.map((day) => (
                      <Button key={day.id} variant="outline"
                        className="h-auto py-3 px-3 flex flex-col items-start gap-1 hover:border-primary hover:bg-primary/90"
                        onClick={() => startFromTemplate(routine, day)}>
                        <span className="font-medium text-sm">{day.day_name}</span>
                        <span className="text-xs text-muted-foreground">{day.exercises?.length || 0} exercises</span>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Workout Preferences Sheet ──────────────────────────── */}
      <Sheet open={showPreferences} onOpenChange={setShowPreferences}>
        <SheetContent side="right" className="w-full sm:max-w-sm overflow-y-auto" data-testid="workout-preferences-sheet">
          <SheetHeader className="mb-6">
            <SheetTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="flex items-center gap-2 text-xl">
              <SlidersHorizontal className="h-5 w-5 text-primary" />
              {t('workout.preferencesTitle')}
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-6">
            {/* Rest Timer */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-1 border-b border-border/50">
                <Timer className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('workout.prefRestTimer')}</h3>
                <InfoButton contentKey="restTimer" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('workout.prefDefaultRestTime')}</p>
                  <p className="text-xs text-muted-foreground">{t('workout.prefSecondsBetweenSets')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min="10" max="600" step="5"
                    value={workoutPrefs.defaultRestSeconds}
                    onChange={(e) => updatePref('defaultRestSeconds', Math.max(10, parseInt(e.target.value) || 90))}
                    className="w-20 h-8 text-center text-sm"
                    data-testid="pref-rest-seconds"
                  />
                  <span className="text-xs text-muted-foreground">{t('workout.sec')}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    {workoutPrefs.restBeepEnabled ? <Volume2 className="h-4 w-4 text-primary" /> : <VolumeX className="h-4 w-4 text-muted-foreground" />}
                    {t('workout.prefBeepWhenRest')}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('workout.prefAudioAlert')}</p>
                </div>
                <Switch
                  checked={workoutPrefs.restBeepEnabled}
                  onCheckedChange={(v) => updatePref('restBeepEnabled', v)}
                  data-testid="pref-rest-beep"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <Zap className="h-4 w-4 text-yellow-500" />
                    {t('workout.prefAutoStartRest')}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('workout.prefAutoStartRestDesc')}</p>
                </div>
                <Switch
                  checked={workoutPrefs.autoStartRestTimer}
                  onCheckedChange={(v) => updatePref('autoStartRestTimer', v)}
                  data-testid="pref-auto-rest"
                />
              </div>
            </div>

            {/* Progression & Deload */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-1 border-b border-border/50">
                <TrendingUp className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('workout.prefProgressionDeload')}</h3>
              </div>

              {/* Full Auto Progression Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl border-2 border-dashed border-primary/30 bg-primary/10">
                <div>
                  <p className="text-sm font-bold flex items-center gap-1.5 text-primary">
                    {t('workout.prefFullAutoProg')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('workout.prefFullAutoProgDesc')}</p>
                </div>
                <button
                  onClick={() => updatePref('fullAutoProgression', !workoutPrefs.fullAutoProgression)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${workoutPrefs.fullAutoProgression ? 'bg-primary' : 'bg-muted'}`}
                  data-testid="pref-full-auto-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${workoutPrefs.fullAutoProgression ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Progression Badges Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <Target className="h-3.5 w-3.5 text-amber-400" />
                    {t('workout.prefShowBadges')}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('workout.prefShowBadgesDesc')}</p>
                </div>
                <button
                  onClick={() => updatePref('showProgressionBadges', !workoutPrefs.showProgressionBadges)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${workoutPrefs.showProgressionBadges ? 'bg-primary' : 'bg-muted'}`}
                  data-testid="pref-show-badges-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${workoutPrefs.showProgressionBadges ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <TrendingUp className="h-3.5 w-3.5 text-green-500" />
                    {t('workout.prefAutoProgressStep')}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('workout.prefAutoProgressStepDesc')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min="0.5" step="0.5"
                    value={workoutPrefs.autoProgressStep}
                    onChange={(e) => updatePref('autoProgressStep', Math.max(0.5, parseFloat(e.target.value) || 2.5))}
                    className="w-20 h-8 text-center text-sm"
                    data-testid="pref-progress-step"
                  />
                  <span className="text-xs text-muted-foreground">kg</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <TrendingDown className="h-3.5 w-3.5 text-red-500" />
                    {t('workout.prefAutoDeloadStep')}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('workout.prefAutoDeloadStepDesc')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min="0.5" step="0.5"
                    value={workoutPrefs.autoDeloadStep}
                    onChange={(e) => updatePref('autoDeloadStep', Math.max(0.5, parseFloat(e.target.value) || 2.5))}
                    className="w-20 h-8 text-center text-sm"
                    data-testid="pref-deload-step"
                  />
                  <span className="text-xs text-muted-foreground">kg</span>
                </div>
              </div>
            </div>

            {/* Display & UX */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-1 border-b border-border/50">
                <Monitor className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('workout.prefDisplayUX')}</h3>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <Target className="h-3.5 w-3.5 text-primary" />
                    {t('workout.prefRPETracking')}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('workout.prefRPETrackingDesc')}</p>
                </div>
                <Switch
                  checked={workoutPrefs.rpeTrackingEnabled}
                  onCheckedChange={(v) => updatePref('rpeTrackingEnabled', v)}
                  data-testid="pref-rpe-toggle"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('workout.prefKeepScreenOn')}</p>
                  <p className="text-xs text-muted-foreground">{t('workout.prefKeepScreenOnDesc')}</p>
                </div>
                <Switch
                  checked={workoutPrefs.keepScreenOn}
                  onCheckedChange={(v) => updatePref('keepScreenOn', v)}
                  data-testid="pref-keep-screen"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('workout.prefDefaultReps')}</p>
                  <p className="text-xs text-muted-foreground">{t('workout.prefDefaultRepsDesc')}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number" min="1" max="50"
                    value={workoutPrefs.defaultRepsMin}
                    onChange={(e) => updatePref('defaultRepsMin', Math.max(1, parseInt(e.target.value) || 8))}
                    className="w-14 h-8 text-center text-sm"
                    data-testid="pref-reps-min"
                  />
                  <span className="text-xs text-muted-foreground">–</span>
                  <Input
                    type="number" min="1" max="50"
                    value={workoutPrefs.defaultRepsMax}
                    onChange={(e) => updatePref('defaultRepsMax', Math.max(1, parseInt(e.target.value) || 12))}
                    className="w-14 h-8 text-center text-sm"
                    data-testid="pref-reps-max"
                  />
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full text-muted-foreground"
              onClick={() => {
                updatePrefs(DEFAULT_WORKOUT_PREFS);
                toast.success(t('workout.prefsReset'));
              }}
              data-testid="pref-reset-btn"
            >
              {t('workout.prefResetBtn')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Muscle Detail Modal */}
      <MuscleDetailModal
        muscleStats={wkMuscleDetailStats}
        isOpen={wkMuscleDetailOpen}
        onClose={() => setWkMuscleDetailOpen(false)}
        routineName={wkMuscleDetailName}
        colorMode="daily"
      />

      <RoutineCreateChooser
        open={chooserOpen}
        onOpenChange={setChooserOpen}
        mode="personal"
        personalRoutinesCount={personalRoutines.length}
        maxPersonalRoutines={MAX_CLIENT_ROUTINES}
        onPickScratch={handlePickScratch}
        onPickWizard={handlePickWizard}
      />

      <RoutineWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        mode="personal"
        onSave={handleWizardSave}
      />

      <WeekPlannerModal
        open={!!weekPlannerRoutine}
        onClose={() => setWeekPlannerRoutine(null)}
        routine={weekPlannerRoutine}
        isPersonal
        onSaved={() => {
          setWeekPlannerRoutine(null);
          queryClient.invalidateQueries({ queryKey: ['personalRoutines'] });
        }}
      />
    </div>
  );
}
